<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import L from 'leaflet';
import { SHARED_ROUTE_PALETTE } from '../utils/palette';
import 'leaflet/dist/leaflet.css';

const props = defineProps({
  clusters: {
    type: Array,
    required: true,
    // [{ lat, lon, count }]
  },
});

const emit = defineEmits(['zone-select', 'zone-confirm']);

const mapEl = ref(null);
let map = null;
const circles = [];

// ── Colour palette (distinct, accessible) ─────────────────
const PALETTE = SHARED_ROUTE_PALETTE.slice(0, 8);

/**
 * Greedy graph colouring. Two zones are adjacent when their 10 km circles
 * overlap, i.e. distance between centres < 20 km.
 */
function assignColours(clusters) {
  const n = clusters.length;
  const ZONE_RADIUS_KM = 10;

  function haversine(a, b) {
    const R = 6371;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLon = (b.lon - a.lon) * Math.PI / 180;
    const x =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(a.lat * Math.PI / 180) *
      Math.cos(b.lat * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }

  // Build adjacency list
  const adj = Array.from({ length: n }, () => []);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (haversine(clusters[i], clusters[j]) < ZONE_RADIUS_KM * 2) {
        adj[i].push(j);
        adj[j].push(i);
      }
    }
  }

  // Greedy colouring
  const colours = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const used = new Set(adj[i].map((j) => colours[j]).filter((c) => c !== null));
    colours[i] = PALETTE.find((c) => !used.has(c)) ?? PALETTE[0];
  }
  return colours;
}

const selectedIdx = ref(null);

function buildMap() {
  if (!mapEl.value || !props.clusters.length) return;

  map = L.map(mapEl.value, { zoomControl: true, attributionControl: true });

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap contributors',
  }).addTo(map);

  const colours = assignColours(props.clusters);
  const bounds = [];

  props.clusters.forEach((c, idx) => {
    const colour = colours[idx];

    const circle = L.circle([c.lat, c.lon], {
      radius: 10_000, // metres
      color: colour,
      weight: 2,
      fillColor: colour,
      fillOpacity: 0.32,
    }).addTo(map);

    circle.bindTooltip(`${c.count} routes`, {
      permanent: false,
      direction: 'center',
      className: 'zone-tooltip',
    });

    circle.on('click', () => {
      if (selectedIdx.value === idx) {
        // Second tap on the same zone — confirm
        emit('zone-confirm', { lat: c.lat, lon: c.lon });
        return;
      }
      selectedIdx.value = idx;
      // Highlight selected, dim others
      circles.forEach((circ, i) => {
        circ.setStyle({
          fillOpacity: i === idx ? 0.55 : 0.15,
          weight: i === idx ? 3 : 2,
        });
      });
      emit('zone-select', { lat: c.lat, lon: c.lon });
    });

    circle.on('mouseover', () => {
      if (selectedIdx.value !== idx) {
        circle.setStyle({ fillOpacity: 0.4 });
      }
    });
    circle.on('mouseout', () => {
      if (selectedIdx.value !== idx) {
        circle.setStyle({ fillOpacity: 0.25 });
      }
    });

    circles.push(circle);
    bounds.push([c.lat, c.lon]);
  });

  // Centre on the busiest cluster (clusters are sorted by descending count).
  if (props.clusters.length) {
    const top = props.clusters[0];
    map.setView([top.lat, top.lon], 9);
  }
}

function teardownMap() {
  if (map) {
    map.remove();
    map = null;
  }
  circles.length = 0;
  selectedIdx.value = null;
}

onMounted(async () => {
  await nextTick();
  buildMap();
});

watch(
  () => props.clusters,
  async () => {
    teardownMap();
    await nextTick();
    buildMap();
  },
);

onBeforeUnmount(() => {
  teardownMap();
});
</script>

<template>
  <div class="zone-picker">
    <p class="zone-picker-hint">
      Tap a zone to set your starting area
    </p>
    <div
      ref="mapEl"
      class="zone-map"
    />
  </div>
</template>

<style scoped>
.zone-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.zone-picker-hint {
  margin: 0;
  font-size: 0.88rem;
  color: #666;
  text-align: center;
}

.zone-map {
  width: 100%;
  height: 320px;
  border-radius: 8px;
  border: 1px solid #ccc;
  overflow: hidden;
}

@media (min-width: 720px) {
  .zone-map {
    height: 380px;
  }
}
</style>

<!-- Global tooltip style (can't be scoped since Leaflet injects outside component root) -->
<style>
.zone-tooltip {
  background: rgba(0, 0, 0, 0.75);
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 0.82rem;
  padding: 3px 8px;
  white-space: nowrap;
}
.zone-tooltip::before {
  display: none;
}
</style>
